// src/components/CheckingAssistant.js — audio-guided checking assistant.
// Reads each line item's product name, batch, MRP, and quantity aloud
// using the browser's built-in speech synthesis (no external service or
// cost), one item at a time, alongside a visual list of the same data.
// Speed is adjustable; advancing is manual by default (checker's own
// pace) with an optional auto-advance toggle.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { get } from '../utils/api';
import { Spinner } from './Shared';

// Marg encodes pack sizes like "15^S" to mean "15's" (a strip/pack of
// 15 units) — read/shown literally, "^" sounds wrong and looks wrong.
// Clean it up for both display and speech.
function cleanProductName(name) {
  if (!name) return name;
  return name.replace(/(\d+)\^S\b/gi, "$1's").replace(/\^/g, ' ').replace(/\s+/g, ' ').trim();
}

// Picks the best available Indian-accented English voice, if the
// browser/OS has one installed — falls back to the default voice
// otherwise. Voice lists load asynchronously in some browsers, so this
// is called fresh each time speech starts rather than cached once.
function pickVoice() {
  const voices = window.speechSynthesis.getVoices();
  return (
    voices.find(v => v.lang === 'en-IN') ||
    voices.find(v => v.lang?.startsWith('en-IN')) ||
    voices.find(v => v.name?.toLowerCase().includes('india')) ||
    null // null = browser's default voice
  );
}

export default function CheckingAssistant({ invoiceNumber, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [speaking, setSpeaking] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(false);

  const utteranceRef = useRef(null);
  const chosenVoiceRef = useRef(null);
  const voiceReadyRef = useRef(false);

  // Voices load asynchronously in the browser — the very first call to
  // getVoices() can return an incomplete list (picking a fallback
  // voice), while a later call after the full list has loaded picks a
  // different one. Pick the voice exactly once, here, and reuse that
  // same choice for every item so it never changes mid-session.
  useEffect(() => {
    function settleVoice() {
      if (voiceReadyRef.current) return;
      const voices = window.speechSynthesis.getVoices();
      if (!voices.length) return; // not loaded yet — wait for the event below
      chosenVoiceRef.current = pickVoice();
      voiceReadyRef.current = true;
    }
    settleVoice();
    window.speechSynthesis.onvoiceschanged = settleVoice;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    get(`/api/checking/invoice/${invoiceNumber}`)
      .then(data => {
        if (!cancelled) {
          setItems(data.items || []);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      window.speechSynthesis.cancel();
    };
  }, [invoiceNumber]);

  const speakItem = useCallback((index) => {
    window.speechSynthesis.cancel();
    const item = items[index];
    if (!item) return;

    const name = cleanProductName(item.product_name) || `Product code ${item.product_code || item.pid}`;
    const segments = [
      name,
      item.batch ? `Batch ${item.batch}` : null,
      item.expiry ? `Expiry ${new Date(item.expiry).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}` : null,
      item.mrp != null ? `M R P ${item.mrp} rupees` : null,
      item.qty != null ? `Quantity ${item.qty}` : null,
    ].filter(Boolean);

    // A single utterance with period-separated segments, rather than
    // chained separate utterances — Chrome's speech engine has a real,
    // known startup stall when repeatedly starting new utterances back
    // to back, which produced multi-second gaps regardless of our own
    // delay value. One utterance avoids that entirely; the pause you
    // hear between segments is just the engine's natural, short
    // sentence-boundary pause.
    const voice = chosenVoiceRef.current;
    const utterance = new SpeechSynthesisUtterance(segments.join('. '));
    utterance.rate = speed;
    if (voice) utterance.voice = voice;
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => {
      setSpeaking(false);
      if (autoAdvance && index < items.length - 1) {
        setCurrent(index + 1);
      }
    };
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [items, speed, autoAdvance]);

  // Re-speak whenever the current item or speed changes (but not on
  // every keystroke elsewhere) — this is what makes "next" actually
  // read the new item aloud immediately.
  useEffect(() => {
    if (items.length && !loading) {
      speakItem(current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, items, loading]);

  function stopSpeaking() {
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }

  function repeat() {
    speakItem(current);
  }

  function goNext() {
    if (current < items.length - 1) setCurrent(current + 1);
  }

  function goPrev() {
    if (current > 0) setCurrent(current - 1);
  }

  function changeSpeed(newSpeed) {
    setSpeed(newSpeed);
    // Apply immediately to whatever's currently playing
    if (speaking) {
      window.speechSynthesis.cancel();
      setTimeout(() => speakItem(current), 50);
    }
  }

  const item = items[current];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
         onClick={() => { window.speechSynthesis.cancel(); onClose(); }}>
      <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '760px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
           onClick={e => e.stopPropagation()}>

        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ fontWeight: 700, fontSize: '15px', color: '#1f2937', margin: 0 }}>🔊 Checking Assistant — {invoiceNumber}</p>
            {!loading && !error && <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0 0' }}>Item {current + 1} of {items.length}</p>}
          </div>
          <button onClick={() => { window.speechSynthesis.cancel(); onClose(); }}
            style={{ background: 'none', border: 'none', fontSize: '22px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {loading && <div style={{ textAlign: 'center', padding: '40px' }}><Spinner /></div>}
          {error && (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#991b1b' }}>
              {error}
            </div>
          )}
          {!loading && !error && item && (
            <>
              {/* Currently-read item, large and clear */}
              <div style={{ backgroundColor: '#eff6ff', border: '2px solid #2563eb', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                <p style={{ fontSize: '18px', fontWeight: 700, color: '#1f2937', margin: '0 0 12px 0' }}>
                  {cleanProductName(item.product_name) || `Unnamed product (code ${item.product_code || item.pid})`}
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                  <div>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Batch</p>
                    <p style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', margin: 0 }}>{item.batch || '—'}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Expiry</p>
                    <p style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', margin: 0 }}>
                      {item.expiry ? new Date(item.expiry).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—'}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>MRP</p>
                    <p style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', margin: 0 }}>{item.mrp != null ? `₹${item.mrp}` : '—'}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Quantity</p>
                    <p style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', margin: 0 }}>{item.qty ?? '—'}</p>
                  </div>
                </div>
              </div>

              {/* Playback controls */}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '16px' }}>
                <button onClick={goPrev} disabled={current === 0}
                  style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', cursor: current === 0 ? 'default' : 'pointer', opacity: current === 0 ? 0.4 : 1 }}>
                  ◀ Prev
                </button>
                <button onClick={speaking ? stopSpeaking : repeat}
                  style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', backgroundColor: speaking ? '#dc2626' : '#2563eb', color: 'white', fontWeight: 600, cursor: 'pointer' }}>
                  {speaking ? '⏸ Stop' : '🔊 Repeat'}
                </button>
                <button onClick={goNext} disabled={current === items.length - 1}
                  style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', cursor: current === items.length - 1 ? 'default' : 'pointer', opacity: current === items.length - 1 ? 0.4 : 1 }}>
                  Next ▶
                </button>
              </div>

              {/* Speed control */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Speed:</span>
                {[0.5, 0.75, 1, 1.25, 1.5, 2].map(s => (
                  <button key={s} onClick={() => changeSpeed(s)}
                    style={{
                      padding: '4px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
                      border: speed === s ? '2px solid #2563eb' : '1px solid #d1d5db',
                      backgroundColor: speed === s ? '#eff6ff' : 'white',
                      color: speed === s ? '#2563eb' : '#6b7280',
                      fontWeight: speed === s ? 700 : 400,
                    }}>
                    {s}×
                  </button>
                ))}
              </div>

              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                <input type="checkbox" checked={autoAdvance} onChange={e => setAutoAdvance(e.target.checked)} />
                Auto-advance to next item when done speaking
              </label>

              {/* Full visual bill list */}
              <div style={{ marginTop: '24px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                <p style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: '10px' }}>FULL BILL</p>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 0.8fr 0.6fr', gap: '4px 12px', fontSize: '11px', color: '#9ca3af', fontWeight: 600, padding: '0 10px 6px 10px' }}>
                  <span>PRODUCT</span>
                  <span>BATCH</span>
                  <span>EXPIRY</span>
                  <span>MRP</span>
                  <span>QTY</span>
                </div>
                {items.map((it, i) => (
                  <div key={it.id} onClick={() => setCurrent(i)}
                    style={{
                      display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 0.8fr 0.6fr', gap: '4px 12px',
                      alignItems: 'center', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: i === current ? '#eff6ff' : 'transparent',
                      fontWeight: i === current ? 600 : 400,
                    }}>
                    <span style={{ fontSize: '13px', color: '#1f2937' }}>{cleanProductName(it.product_name) || `Code ${it.product_code || it.pid}`}</span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>{it.batch || '—'}</span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {it.expiry ? new Date(it.expiry).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—'}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>₹{it.mrp ?? '—'}</span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>{it.qty ?? '—'}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
